/* eslint-disable no-undef */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';

import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ColorLensIcon from '@mui/icons-material/ColorLens';

import { ComponentLogin } from './ComponentLogin';
import { DialogBox } from '../../../../components/singleComponents/dialogs/DialogBox';
import ToasterBox from '../../../../components/singleComponents/toaster/ToasterBox';

import api, { authHeader } from '../../../../../services/API/apiConfig';
import GlobalContext from '../../../../../context/GlobalContext';

import {
  ButtonPrimaryStyled,
  ButtonSecondaryStyled,
  IconFolderStyled,
  IconStyled,
  UploadLogoStyled,
  UploadStyled,
} from './Personalization.styles';

export function Personalization() {
  const colorInputRef = useRef(null);
  const { companyData } = useContext(GlobalContext);

  const { getRootProps, getInputProps, acceptedFiles, isDragActive } =
    useDropzone({
      accept: 'image/jpeg, image/png, image/svg, image/jpg',
    });

  const primary = companyData.primary_color.replace('#', '');
  const secondary = companyData.secondary_color.replace('#', '');

  const [errorMessage, setErrorMessage] = useState('');
  const [errorSubmit, setErrorSubmit] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(primary);
  const [secondaryColor, setSecondaryColor] = useState(secondary);
  const [logo, setLogo] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const handleIconClick = () => {
    if (colorInputRef.current) {
      colorInputRef.current.click(); // Programmatically click the input
    }
  };

  function closeDialog() {
    setOpenDialog(!openDialog);
  }
  function closeCancelDialog() {
    setOpenCancelDialog(!openCancelDialog);
  }

  function handleSetPrimaryColor(event) {
    let { value } = event.target;
    value = value.replace(/\s+/g, '').replace(/[^a-z0-9]/gi, '');
    setPrimaryColor(value);
  }
  function handleSetSecondaryColor(event) {
    let { value } = event.target;
    value = value.replace(/\s+/g, '').replace(/[^a-z0-9]/gi, '');
    setSecondaryColor(value);
  }

  useEffect(() => {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
    setLogo(companyData.logo.url);
  }, [companyData, primary, secondary]);

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      const { name, size, path, type, lastModified, lastModifiedDate } =
        acceptedFiles[0];
      const preview = URL.createObjectURL(acceptedFiles[0]);
      const image = {
        name,
        size,
        path,
        type,
        lastModified,
        lastModifiedDate,
        preview,
      };
      setLogo(image);
    }
  }, [acceptedFiles]);

  function handleCancelPersonalization() {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
    setLogo([]);
    setOpenCancelDialog(false);
  }

  function changeSystemColor() {
    const fd = new FormData();
    fd.append('name', companyData.name);
    fd.append('primary_color', `#${primaryColor}`);
    fd.append('secondary_color', `#${secondaryColor}`);
    fd.append('document', companyData.document);
    if (acceptedFiles[0] !== undefined) fd.append('logo', acceptedFiles[0]);

    api
      .patch(`/companies/${companyData.id}`, fd, {
        headers: {
          ...authHeader(),
          'Content-Type': `multipart/form-data; boundary=${fd._boundary}`,
        },
      })
      .then(() => {
        setOpenDialog(!openDialog);
      })
      .catch(() => {
        setErrorSubmit(true);
        setErrorMessage('Erro ao tentar personalizar o sistema');
      })
      .finally(() => {
        setTimeout(() => {
          setErrorSubmit(false);
          setErrorMessage('');
        }, 3000);
      });
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', padding: 32 }}>
        {errorSubmit && <ToasterBox message={errorMessage} />}
        <div style={{ width: '50%', marginTop: 32 }}>
          <div style={{ gap: 16 }}>
            <Typography variant="h2" style={{ marginBottom: 24 }}>
              Alterar logo
            </Typography>
            <UploadStyled>
              <IconFolderStyled {...getRootProps()}>
                <input {...getInputProps()} multiple={false} />
                <IconStyled />
              </IconFolderStyled>
              <UploadLogoStyled {...getRootProps()}>
                <input {...getInputProps()} multiple={false} />
                {isDragActive ? (
                  <Typography variant="body1">Solte a imagem</Typography>
                ) : (
                  <Typography variant="body1">Arraste o logo</Typography>
                )}
              </UploadLogoStyled>
            </UploadStyled>
          </div>
          <div style={{ marginTop: 32 }}>
            <Typography variant="h2">Alterar cor</Typography>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginTop: 32,
              }}
            >
              <div style={{ display: 'flex', gap: 8, width: '80%' }}>
                <TextField
                  inputRef={colorInputRef}
                  label="Cor primária"
                  type="color"
                  variant="outlined"
                  value={`#${primaryColor}`}
                  onChange={(event) => handleSetPrimaryColor(event)}
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton onClick={handleIconClick}>
                            <ColorLensIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  inputRef={colorInputRef}
                  label="Cor secundária"
                  type="color"
                  variant="outlined"
                  value={`#${secondaryColor}`}
                  onChange={(event) => handleSetSecondaryColor(event)}
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton onClick={handleIconClick}>
                            <ColorLensIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div style={{ height: '100%', border: '0.6px solid #777' }}>
          <ComponentLogin
            primary={`#${primaryColor}`}
            secondary={`#${secondaryColor}`}
            logo={logo}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: 32,
        }}
      >
        <div />
        <div style={{ gap: 32, display: 'flex' }}>
          <ButtonPrimaryStyled
            color="secondary"
            variant="contained"
            onClick={() => changeSystemColor()}
          >
            Personalizar
          </ButtonPrimaryStyled>
          <ButtonSecondaryStyled
            variant="contained"
            onClick={() => setOpenCancelDialog(true)}
          >
            Cancelar
          </ButtonSecondaryStyled>
        </div>
      </div>
      <DialogBox
        title="Alterações realizadas com sucesso!"
        content="Atenção! As modificações serão aplicadas após reiniciar o sistema. Deseja reiniciar agora?"
        openDialog={openDialog}
        closeDialog={() => closeDialog()}
        mainAction={() => window.location.reload()}
      />
      <DialogBox
        title="Cancelar alterações!"
        content="Deseja realmente cancelar as alterações?"
        openDialog={openCancelDialog}
        closeDialog={() => closeCancelDialog()}
        mainAction={() => handleCancelPersonalization()}
      />
    </>
  );
}
